import { createSlice } from '@reduxjs/toolkit';
import {
    createTrip, searchTrip, cancelTrip, getOneTrip, tripStageSearch,
    getTripLog, getTripSummary, updateRoutePathFromTrip, updateTripSettings,
    tripReprocess, resetTrip, resetAllTrips, forceCompleteTrip, getActiveTrips,
    getProblemTrips, getTripPerformance, exportTripPerformance, tripReportPdf
} from './tripsApi';

const tripControlSlice = createSlice({
    name: 'tripControl',
    initialState: {
        selectedTrip: null,
        tripList: [],
        creatingTrip: false,
        creatingTripError: null,
        searchingTrip: false,
        searchStage: null,
        tripLog: null,
        tripLogLoading: false,
        tripSummary: null,
        tripSummaryLoading: false,
        tripPerformance: null,
        tripPerformanceLoading: false,
        tripPerformanceError: null,
        tripPerformanceCacheKey: null,
        tripReportPdfLoading: false,
        tripReportPdfError: null,
        updatingRoutePath: false,
        updatingTripSettings: false,
        showTripReport: false,
        tripReprocessing: false,
        tripPageIndex: 1,
        tripsPerPage: 10,
        pagination: null,
    },
    reducers: {
        setSelectedTrip: (state, action) => {
            state.selectedTrip = action.payload;
        },
        clearTripCreationError: (state) => {
            state.creatingTripError = null;
        },
        setSearchStage: (state, action) => {
            state.searchStage = action.payload;
        },
        setShowTripReport: (state, action) => {
            state.showTripReport = action.payload;
        },
        setTripPageIndex: (state, action) => {
            state.tripPageIndex = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createTrip.pending, (state) => {
                state.selectedTrip = null;
                state.creatingTrip = true;
                state.creatingTripError = null;
            })
            .addCase(createTrip.fulfilled, (state, action) => {
                state.selectedTrip = action.payload.trip;
                state.creatingTrip = false;
                state.creatingTripError = null;
            })
            .addCase(createTrip.rejected, (state, action) => {
                state.selectedTrip = null;
                state.creatingTrip = false;
                state.creatingTripError = action.error.message || 'Failed to create trip';
            })
            .addCase(searchTrip.pending, (state) => {
                state.tripList = [];
                state.searchingTrip = true;
            })
            .addCase(searchTrip.fulfilled, (state, action) => {
                let trips = action.payload.trips;
                trips.forEach(trip => {
                    trip.stageColor = statusColor(trip.tripStage);
                    trip.activeStatusColor = statusColor(trip.activeStatus);
                    trip.locMeta = tripLocMetaGenerator(trip);
                });
                state.tripList = tripSorter(trips);
                state.searchingTrip = false;
            })
            .addCase(searchTrip.rejected, (state, action) => {
                state.tripList = [];
                state.searchingTrip = false;
            })
            .addCase(cancelTrip.fulfilled, (state, action) => {
                const updatedTrip = action.payload.updatedTrip;
                const tripIndex = state.tripList.findIndex(trip => trip._id === updatedTrip._id);
                if (tripIndex !== -1) {
                    state.tripList[tripIndex].status = updatedTrip.status;
                    state.tripList[tripIndex].abortReason = updatedTrip.abortReason;
                }
            })
            .addCase(getOneTrip.fulfilled, (state, action) => {
                const trip = action.payload.trip;
                trip.stageColor = statusColor(trip.tripStage);
                trip.activeStatusColor = statusColor(trip.activeStatus);
                trip.locMeta = tripLocMetaGenerator(trip);
                if (trip.violationsCount == null) {
                    const ruleViolationCount = Array.isArray(trip.significantEvents)
                        ? trip.significantEvents.filter(event => event.eventType === 'ruleViolation').length
                        : 0;
                    trip.violationsCount = ruleViolationCount;
                }
                trip.fullyLoaded = true;
                state.selectedTrip = trip;
            })
            .addCase(getOneTrip.rejected, (state, action) => {
                state.selectedTrip.fullyLoaded = false;
                console.log("Get one trip rejected", action);
            })
            .addCase(tripStageSearch.pending, (state) => {
                state.searchingTrip = true;
            })
            .addCase(tripStageSearch.fulfilled, (state, action) => {
                let trips = action.payload.trips;
                state.tripList = tripSorter(trips);
                state.searchingTrip = false;
            })
            .addCase(tripStageSearch.rejected, (state, action) => {
                console.log("Trip stage search rejected", action);
                state.searchingTrip = false;
            })
            .addCase(getActiveTrips.pending, (state) => {
                state.searchingTrip = true;
            })
            .addCase(getActiveTrips.fulfilled, (state, action) => {
                state.tripList = action.payload.trips;
                state.searchingTrip = false;
            })
            .addCase(getTripLog.pending, (state) => {
                state.tripLog = null;
                state.tripLogLoading = true;
            })
            .addCase(getTripLog.fulfilled, (state, action) => {
                state.tripLog = action.payload.logContent;
                state.tripLogLoading = false;
            })
            .addCase(getTripLog.rejected, (state, action) => {
                console.log("Get trip log rejected", action);
                state.tripLogLoading = false;
            })
            .addCase(getTripSummary.pending, (state) => {
                state.tripSummary = null;
                state.tripSummaryLoading = true;
            })
            .addCase(getTripSummary.fulfilled, (state, action) => {
                //state.tripSummary = buildSummary(action.payload);
                console.log("getTripSummary.fulfilled", action.payload);
                state.tripSummary = action.payload;
                state.tripSummaryLoading = false;
            })
            .addCase(getTripSummary.rejected, (state, action) => {
                console.log("Get trip summary rejected", action);
                state.tripSummaryLoading = false;
            })
            .addCase(getTripPerformance.pending, (state) => {
                state.tripPerformanceLoading = true;
                state.tripPerformanceError = null;
            })
            .addCase(getTripPerformance.fulfilled, (state, action) => {
                const { cacheKey, ...data } = action.payload || {};
                state.tripPerformance = data;
                state.tripPerformanceCacheKey = cacheKey || state.tripPerformanceCacheKey;
                state.tripPerformanceLoading = false;
            })
            .addCase(getTripPerformance.rejected, (state, action) => {
                state.tripPerformanceLoading = false;
                state.tripPerformanceError = action.payload || action.error?.message || 'Failed to load trip performance';
            })
            .addCase(tripReportPdf.pending, (state) => {
                state.tripReportPdfLoading = true;
                state.tripReportPdfError = null;
            })
            .addCase(tripReportPdf.fulfilled, (state) => {
                state.tripReportPdfLoading = false;
            })
            .addCase(tripReportPdf.rejected, (state, action) => {
                state.tripReportPdfLoading = false;
                state.tripReportPdfError = action.payload || action.error?.message || 'Failed to download trip report';
            })
            .addCase(updateRoutePathFromTrip.pending, (state) => {
                state.updatingRoutePath = true;
            })
            .addCase(updateRoutePathFromTrip.fulfilled, (state, action) => {
                console.log("Update route path from trip fulfilled", action);
                state.updatingRoutePath = false;
            })
            .addCase(updateRoutePathFromTrip.rejected, (state, action) => {
                console.log("Update route path from trip rejected", action);
                state.updatingRoutePath = false;
            })
            .addCase(updateTripSettings.pending, (state) => {
                state.updatingTripSettings = true;
            })
            .addCase(updateTripSettings.fulfilled, (state, action) => {
                console.log("Update trip settings fulfilled", action);
                state.updatingTripSettings = false;
            })
            .addCase(updateTripSettings.rejected, (state, action) => {
                console.log("Update trip settings rejected", action);
                state.updatingTripSettings = false;
            })
            .addCase(tripReprocess.pending, (state) => {
                state.tripReprocessing = true;
            })
            .addCase(tripReprocess.fulfilled, (state, action) => {
                console.log("Trip reprocessed", action);
            })
            .addCase(resetTrip.pending, (state) => {
                state.resettingTrip = true;
            })
            .addCase(resetTrip.fulfilled, (state, action) => {
                console.log("Trip reset", action);
            })
            .addCase(resetTrip.rejected, (state, action) => {
                console.log("Trip reset rejected", action);
            })
            .addCase(resetAllTrips.pending, (state) => {
                state.resettingAllTrips = true;
            })
            .addCase(resetAllTrips.fulfilled, (state, action) => {
                console.log("All trips reset", action);
                state.resettingAllTrips = false;
            })
            .addCase(resetAllTrips.rejected, (state, action) => {
                console.log("All trips reset rejected", action);
                state.resettingAllTrips = false;
            })
            .addCase(forceCompleteTrip.pending, (state) => {
                state.forceCompletingTrip = true;
            })
            .addCase(forceCompleteTrip.fulfilled, (state, action) => {
                console.log("Trip force completed", action);
            })
            .addCase(getProblemTrips.pending, (state) => {
                state.searchingTrip = true;
            })
            .addCase(getProblemTrips.fulfilled, (state, action) => {
                console.log("Get problem trips fulfilled", action);
                let trips = action.payload.trips;
                trips.forEach(trip => {
                    trip.stageColor = statusColor(trip.tripStage);
                    trip.activeStatusColor = statusColor(trip.activeStatus);
                    trip.locMeta = tripLocMetaGenerator(trip);
                });
                state.tripList = tripSorter(trips);
                state.searchingTrip = false;
            })
            .addCase(getProblemTrips.rejected, (state, action) => {
                console.log("Get problem trips rejected", action);
                state.searchingTrip = false;
            })
    },
});

export const { setSelectedTrip, clearTripCreationError, setSearchStage, setShowTripReport, setTripPageIndex } = tripControlSlice.actions;
export { tripControlSlice };
export {
    createTrip, searchTrip, cancelTrip, getOneTrip, tripStageSearch,
    getTripLog, getTripSummary, updateRoutePathFromTrip, updateTripSettings,
    tripReprocess, resetTrip, resetAllTrips, forceCompleteTrip, getActiveTrips,
    getProblemTrips, getTripPerformance, exportTripPerformance, tripReportPdf
} from './tripsApi';

export default tripControlSlice.reducer;
export const tripControlSelector = (state) => state.tripControl;
export const tripListSelector = (state) => state.tripControl.tripList;
export const selectedTripSelector = (state) => state.tripControl.selectedTrip;
export const creatingTripSelector = (state) => state.tripControl.creatingTrip;
export const searchingTripSelector = (state) => state.tripControl.searchingTrip;
export const creatingTripErrorSelector = (state) => state.tripControl.creatingTripError;
export const searchLoadingSelector = (state) => state.tripControl.searchingTrip;
export const searchStageSelector = (state) => state.tripControl.searchStage;
export const tripLogSelector = (state) => state.tripControl.tripLog;
export const tripLogLoadingSelector = (state) => state.tripControl.tripLogLoading;
export const tripSummarySelector = (state) => state.tripControl.tripSummary;
export const tripSummaryLoadingSelector = (state) => state.tripControl.tripSummaryLoading;
export const tripPerformanceSelector = (state) => state.tripControl.tripPerformance;
export const tripPerformanceLoadingSelector = (state) => state.tripControl.tripPerformanceLoading;
export const tripPerformanceCacheKeySelector = (state) => state.tripControl.tripPerformanceCacheKey;
export const tripPerformanceErrorSelector = (state) => state.tripControl.tripPerformanceError;
export const tripReportPdfLoadingSelector = (state) => state.tripControl.tripReportPdfLoading;
export const showTripReportSelector = (state) => state.tripControl.showTripReport;
export const tripPageIndexSelector = (state) => state.tripControl.tripPageIndex;
export const tripsPerPageSelector = (state) => state.tripControl.tripsPerPage;
export const selectPagination = (state) => state.tripControl.pagination;

const tripSorter = (tripList) => {
    return tripList.sort((a, b) => {
        return new Date(b.plannedStartTime) - new Date(a.plannedStartTime);
    });
}

const statusColor = (status) => {
    const colors = {
        // Planning & Initial States
        'Planned': '#4338CA',      // Darker Indigo
        'Start Delayed': '#B45309', // Darker Amber
        'Active': '#047857',       // Darker Emerald
        'Completed': '#1E40AF',    // Darker Blue

        // Termination States
        'Aborted': '#B91C1C',      // Darker Red
        'Cancelled': '#B91C1C',    // Darker Red
        'Canceled': '#B91C1C',     // Darker Red
        'Inactive': '#4B5563',     // Darker Gray

        // Location States
        'Reached Start Location': '#6D28D9',   // Darker Purple
        'Reached End Location': '#1D4ED8',     // Darker Blue
        'Reached Via Location': '#4338CA',     // Darker Indigo

        // Detention States
        'Detained At Start Location': '#B91C1C',  // Darker Red
        'Detained At Via Location': '#B91C1C',    // Darker Red
        'Detained At End Location': '#B91C1C',    // Darker Red

        // Route States
        'Running On Route': '#065F46',         // Darker Green
        'Running & Route Violated': '#C2410C', // Darker Orange

        // Halt States
        'Halted': '#B45309',                  // Darker Amber
        'Halted & Route Violated': '#C2410C'   // Darker Orange
    }
    return colors[status];
};

const tripLocMetaGenerator = (trip) => {
    const { significantLocations } = trip;
    let locMeta = {};
    significantLocations?.forEach(loc => {
        if (loc.locationType === 'viaLocation') {
            const viaIndex = trip.route.viaLocations.findIndex(viaLoc => viaLoc.locationName === loc.locationName);
            if (viaIndex !== -1) {
                locMeta[`viaLocation${viaIndex + 1}`] = {
                    locationName: loc.locationName,
                    entryTime: loc.entryTime,
                    exitTime: loc.exitTime,
                    dwellTime: loc.dwellTime,
                }
            }
        } else {
            locMeta[loc.locationType] = {
                locationName: loc.locationName,
                entryTime: loc.entryTime,
                exitTime: loc.exitTime,
                dwellTime: loc.dwellTime,
            }
        }
    });
    return locMeta;
}

/* const statusColorbck = (status) => {
    const colors = {
        'Planned': '#2F4F4F', // DarkSlateGrey - Made slightly lighter
        'Start Delayed': '#DB7093', // PaleVioletRed - More muted pink
        'Active': '#008000', // Green - Made darker
        'Completed': '#483D8B', // DarkSlateBlue - Made slightly lighter

        'Aborted': '#B22222', // FireBrick - OK
        'Cancelled': '#B22222', // FireBrick - OK 
        'Canceled': '#B22222', // FireBrick - OK

        'Inactive': '#696969', // DimGrey - Made lighter

        'Reached Start Location': '#1E90FF', // DodgerBlue - OK
        'Reached End Location': '#00A572', // SpringGreen - Made darker
        'Reached Via Location': '#8B008B', // DarkMagenta - Made lighter

        'Detained At Start Location': '#C71585', // MediumVioletRed - Made lighter
        'Detained At Via Location': '#C71585', // MediumVioletRed - Made lighter
        'Detained At End Location': '#C71585', // MediumVioletRed - Made lighter

        'Running On Route': '#228B22', // LimeGreen - Made darker
        'Running & Route Violated': '#FF4500', // OrangeRed - OK

        'Halted': '#B8860B', // GoldenRod - Made darker
        'Halted & Route Violated': '#FF4500', // OrangeRed - OK
    }
    return colors[status];

}; */
